import { createFileRoute } from "@tanstack/react-router";
import {
  LifeBuoy,
  Send,
  Plus,
  MessageCircle,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  X,
  User as UserIcon,
  Shield,
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";

import { ApiError, api } from "@/lib/api";
import { useTickets } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/_app/tickets")({
  component: TicketsPage,
  head: () => ({
    meta: [
      { title: "Support — SmsEmpire" },
      { name: "description", content: "Contact SmsEmpire technical support." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type TicketMessage = {
  id: number;
  ticket_id: number;
  sender_role: string;
  content: string;
  created_at: string;
};

type Ticket = {
  id: number;
  contact_email: string;
  subject: string;
  status: string;
  created_at: string;
  updated_at: string;
  messages: TicketMessage[];
};

import { useI18n } from "@/lib/i18n";
import { formatServerDateTime } from "@/lib/server-time";

function statusColor(s: string) {
  switch (s) {
    case "open":
      return "bg-amber-500/15 text-amber-400 border-amber-500/30";
    case "in_progress":
      return "bg-blue-500/15 text-blue-400 border-blue-500/30";
    case "closed":
      return "bg-muted text-muted-foreground border-border";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

function statusIcon(s: string) {
  switch (s) {
    case "open":
      return <AlertCircle className="h-3.5 w-3.5" />;
    case "in_progress":
      return <Clock className="h-3.5 w-3.5" />;
    case "closed":
      return <CheckCircle2 className="h-3.5 w-3.5" />;
    default:
      return <Clock className="h-3.5 w-3.5" />;
  }
}

function statusLabel(s: string, t: (k: string) => string) {
  switch (s) {
    case "open":
      return t("tickets.status_open");
    case "in_progress":
      return t("tickets.status_in_progress");
    case "closed":
      return t("tickets.status_closed");
    default:
      return s;
  }
}

function TicketsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [showNew, setShowNew] = useState(false);

  // New ticket form
  const [newSubject, setNewSubject] = useState("");
  const [newEmail, setNewEmail] = useState(user?.email || "");
  const [newMessage, setNewMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reply
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Polls only while a ticket is still open, and never with the tab hidden.
  const { data: tickets = [], isPending: loading, refetch: fetchTickets } = useTickets();

  useEffect(() => {
    // Keep the open conversation in sync with the refreshed list.
    if (!selected) return;
    const updated = tickets.find((t) => t.id === selected.id);
    if (updated && updated !== selected) setSelected(updated);
  }, [tickets, selected]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selected?.messages]);

  async function createTicket() {
    if (!newSubject.trim() || !newEmail.trim() || !newMessage.trim()) {
      toast.error(t("tickets.fill_all_fields"));
      return;
    }
    setSubmitting(true);
    try {
      const ticket = await api.post<Ticket>("/api/tickets", {
        contact_email: newEmail,
        subject: newSubject,
        message: newMessage,
      });
      toast.success(t("admin.saved"));
      setShowNew(false);
      setNewSubject("");
      setNewMessage("");
      await fetchTickets();
      setSelected(ticket);
    } catch (error) {
      toast.error(
        error instanceof ApiError && error.isRateLimited
          ? t("tickets.rate_limited")
          : error instanceof ApiError
            ? error.message
            : t("common.error"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function sendReply() {
    if (!reply.trim() || !selected) return;
    setReplying(true);
    try {
      await api.post(`/api/tickets/${selected.id}/messages`, { content: reply });
      setReply("");
      await fetchTickets();
    } catch (error) {
      toast.error(
        error instanceof ApiError && error.isRateLimited
          ? t("tickets.rate_limited")
          : error instanceof ApiError
            ? error.message
            : t("common.error"),
      );
    } finally {
      setReplying(false);
    }
  }

  if (!user) return null;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("tickets.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("tickets.subtitle")}</p>
        </div>
        {!showNew && (
          <Button className="bg-gradient-primary shadow-glow" onClick={() => setShowNew(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("tickets.new_ticket")}
          </Button>
        )}
      </div>

      {/* New Ticket Form */}
      {showNew && (
        <div className="rounded-2xl border border-primary/30 bg-card p-6 shadow-elegant">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">{t("tickets.new_ticket_title")}</h2>
            <Button variant="ghost" size="icon" onClick={() => setShowNew(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="contact-email">{t("tickets.contact_email")}</Label>
              <Input
                id="contact-email"
                type="email"
                placeholder={t("auth.email_placeholder")}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="mt-1"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t("tickets.contact_email_hint")}
              </p>
            </div>
            <div>
              <Label htmlFor="ticket-subject">{t("tickets.subject")}</Label>
              <Input
                id="ticket-subject"
                placeholder={t("tickets.subject_ph")}
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="ticket-message">{t("tickets.message")}</Label>
              <textarea
                id="ticket-message"
                placeholder={t("tickets.message_ph")}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows={4}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-none"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowNew(false)}>
                {t("tickets.cancel")}
              </Button>
              <Button
                onClick={createTicket}
                disabled={submitting}
                className="bg-gradient-primary shadow-glow"
              >
                {submitting ? t("tickets.sending") : t("tickets.send")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Ticket List */}
        <div className="lg:col-span-2 space-y-2">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center">
              <LifeBuoy className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
              <p className="font-medium">{t("tickets.no_tickets")}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t("tickets.no_tickets_desc")}</p>
            </div>
          ) : (
            tickets.map((tItem) => (
              <button
                key={tItem.id}
                onClick={() => setSelected(tItem)}
                className={`w-full rounded-xl border p-4 text-left transition-all hover:border-primary/40 ${
                  selected?.id === tItem.id
                    ? "border-primary/50 bg-primary/5 shadow-glow"
                    : "border-border bg-card"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-sm">{tItem.subject}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      #{tItem.id} · {formatServerDateTime(tItem.updated_at)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${statusColor(tItem.status)}`}
                    >
                      {statusIcon(tItem.status)} {statusLabel(tItem.status, t)}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                </div>
                {tItem.messages.length > 0 && (
                  <p className="mt-2 line-clamp-1 text-xs text-muted-foreground">
                    {tItem.messages[tItem.messages.length - 1].content}
                  </p>
                )}
              </button>
            ))
          )}
        </div>

        {/* Chat View */}
        <div className="lg:col-span-3">
          {!selected ? (
            <div className="flex h-full min-h-[400px] items-center justify-center rounded-2xl border border-dashed border-border">
              <div className="text-center">
                <MessageCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
                <p className="font-medium text-muted-foreground">{t("tickets.select_ticket")}</p>
                <p className="text-sm text-muted-foreground/60">
                  {t("tickets.select_ticket_desc")}
                </p>
              </div>
            </div>
          ) : (
            <div
              className="flex flex-col rounded-2xl border border-border bg-card overflow-hidden"
              style={{ minHeight: 480 }}
            >
              {/* Ticket header */}
              <div className="border-b border-border px-5 py-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold">{selected.subject}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>#{selected.id}</span>
                      <span>·</span>
                      <span>📧 {selected.contact_email}</span>
                      <span>·</span>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${statusColor(selected.status)}`}
                      >
                        {statusIcon(selected.status)} {statusLabel(selected.status, t)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4" style={{ maxHeight: 360 }}>
                {selected.messages.map((m) => {
                  const isAdmin = m.sender_role === "admin";
                  return (
                    <div key={m.id} className={`flex gap-3 ${isAdmin ? "" : "flex-row-reverse"}`}>
                      <div
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                          isAdmin ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {isAdmin ? (
                          <Shield className="h-4 w-4" />
                        ) : (
                          <UserIcon className="h-4 w-4" />
                        )}
                      </div>
                      <div className={`max-w-[80%] ${isAdmin ? "" : "items-end"}`}>
                        <div
                          className={`rounded-2xl px-4 py-2.5 text-sm ${
                            isAdmin
                              ? "bg-primary/10 text-foreground rounded-tl-sm"
                              : "bg-muted text-foreground rounded-tr-sm"
                          }`}
                        >
                          {m.content}
                        </div>
                        <p
                          className={`mt-1 text-xs text-muted-foreground ${isAdmin ? "text-left" : "text-right"}`}
                        >
                          {isAdmin ? t("tickets.support_agent") : t("tickets.you")} ·{" "}
                          {formatServerDateTime(m.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply box */}
              {selected.status !== "closed" ? (
                <div className="border-t border-border p-4">
                  <div className="flex gap-2">
                    <textarea
                      placeholder={t("tickets.reply_placeholder")}
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                      rows={2}
                      className="flex-1 rounded-xl border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                    />
                    <Button
                      onClick={sendReply}
                      disabled={replying || !reply.trim()}
                      className="bg-gradient-primary shadow-glow self-end"
                      size="icon"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">{t("tickets.enter_hint")}</p>
                </div>
              ) : (
                <div className="border-t border-border p-4 text-center text-sm text-muted-foreground">
                  {t("tickets.closed_notice")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
